import { t } from "@/i18n-js/instance";
import { type PaywallCustomization } from "@/react/types/PaywallCustomization";
import { useSpacesContext } from "@circle-react/contexts";
import { usePunditUserContext } from "@circle-react/contexts";
import { paywallCustomizationDefaultHelpers } from "@circle-react/helpers/paywallFormHelpers";
import type { CurrentCommunity } from "@circle-react/types/CurrentCommunity";
import { normalizeAccessGroupSources } from "@circle-react-shared/AccessGroups/helpers";
import type { PaywallForm } from "../../types/PaywallForm";

interface GetPaywallCustomizationDefaultProps {
  brandColor?: CurrentCommunity["prefs"]["brand_color"];
  paywallCustomization?: PaywallCustomization;
  paywallParam?: PaywallForm;
}

const getPaywallCustomizationDefault = ({
  paywallCustomization,
  paywallParam,
  brandColor,
}: GetPaywallCustomizationDefaultProps):
  | Partial<PaywallCustomization>
  | Record<string, never> => ({
  ...(paywallCustomization || {}),
  reduced_price_properties:
    paywallCustomizationDefaultHelpers.reducedPriceProperties({
      reducedPriceProperties: paywallCustomization?.reduced_price_properties,
      paywallParam,
    }),
  countdown_properties: paywallCustomizationDefaultHelpers.countdownProperties(
    paywallCustomization?.countdown_properties,
  ),
  benefit_properties: paywallCustomizationDefaultHelpers.benefitProperties(
    paywallCustomization?.benefit_properties,
  ),
  testimonial_properties:
    paywallCustomizationDefaultHelpers.testimonialProperties(
      paywallCustomization?.testimonial_properties,
    ),
  badge_properties: paywallCustomizationDefaultHelpers.badgeProperties(
    paywallCustomization?.badge_properties,
  ),
  price_callout_properties:
    paywallCustomizationDefaultHelpers.priceCalloutProperties({
      priceCalloutProperties: paywallCustomization?.price_callout_properties,
      paywallParam,
      brandColor,
    }),
});

export const usePaywallFormDefaultValues = () => {
  const { records: spaceRecords } = useSpacesContext();

  const getDefaultSpaceIds = (
    spaceIdsSelected: any,
    spaceGroupIdsSelected: any,
  ) => {
    const newSpaceIdsSelect = [...spaceIdsSelected];
    spaceRecords.forEach((space: any) => {
      if (spaceGroupIdsSelected.includes(space.space_group_id)) {
        newSpaceIdsSelect.push(space.id);
      }
    });

    return Array.from(new Set(newSpaceIdsSelect));
  };

  const { currentCommunitySettings, currentCommunity } = usePunditUserContext();
  const isAccessGroupsEnabled = currentCommunitySettings?.access_groups_enabled;
  const brandColor = currentCommunity?.prefs?.brand_color;

  const getDefaultValues = (paywallParam: any) => {
    const defaultPaywall = {
      trial_days: 0,
      trial_requires_card: false,
      can_be_repurchased: false,
      stripe_prices_attributes: [],
      mobile_price_packages_attributes: [],
      all_spaces: false,
      space_group_ids: [],
      space_ids: [],
      redirect_url_enabled: false,
      thank_you_page_enabled: true,
      post_purchase_email_enabled: true,
      self_trial_cancelable: false,
      mobile_paywall_resource_attributes: {
        id: null,
        processor_id: null,
      },
      post_purchase_email_message: t(
        "settings.paywalls.form.post_purchase_email_default_message_text",
      ),
      post_purchase_email_meta: {
        subject: t(
          "settings.paywalls.form.post_purchase_email_default_subject_text",
        ),
      },
      currency_id: "",
      conversion_tracking_js_code: "",
      access_groups_enabled: isAccessGroupsEnabled,
      access_groups: [],
      paywall_customization_attributes: {
        ...getPaywallCustomizationDefault({ brandColor }),
      },
    };

    if (!paywallParam) {
      return defaultPaywall;
    }

    return {
      ...defaultPaywall,
      ...paywallParam,
      redirect_url_enabled: !!paywallParam?.redirect_url,
      enable_trial: paywallParam?.trial_days > 0,
      enable_subscription_group: !!paywallParam?.paywall_group_id,
      space_ids: getDefaultSpaceIds(
        paywallParam.space_ids,
        paywallParam.space_group_ids,
      ),
      space_group_ids: paywallParam.space_group_ids ?? [],
      currency_id: paywallParam?.currency?.id ?? "",
      access_groups_enabled: paywallParam?.access_groups_enabled,
      access_groups: normalizeAccessGroupSources(
        paywallParam?.access_group_sources,
      ),
      paywall_customization_attributes: {
        ...getPaywallCustomizationDefault({
          paywallCustomization: paywallParam?.paywall_customization,
          paywallParam,
          brandColor,
        }),
      },
    };
  };

  return {
    getDefaultValues,
  };
};
